import React, { useEffect, useState } from 'react';
import PageBackNavigation from '../../components/PageBackNavigation';
import WorkGallery from '../../components/WorkGallery';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isReadOnlyTeacherDemo } from '../../utils/demoAccess';
import { GRADING_STYLES, pointColor } from '../../utils/grading';

export default function StudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null;
  const returnLabel = returnTo?.label || '← Back to Profiles';
  const { token, user } = useAuth();
  const [student, setStudent] = useState<any>(null);
  const [grades, setGrades] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');

  const handleBack = () => {
    if (returnTo?.pathname) {
      navigate(returnTo.pathname, returnTo.state ? { state: returnTo.state } : undefined);
      return;
    }

    navigate('/teacher/student-profiles');
  };

  useEffect(() => {
    async function loadStudent() {
      setLoading(true);
      setError('');
      try {
        // Always use the API endpoint, not a frontend route
        let apiUrl = `/api/teacher/students/${studentId}`;
        // Optionally, use an absolute URL in production if needed
        // if (window.location.hostname !== 'localhost') {
        //   apiUrl = `https://app.academiceye.net/api/teacher/students/${studentId}`;
        // }
        const res = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) throw new Error('Student not found');
        let data;
        try {
          data = await res.json();
        } catch (e: any) {
          console.warn('Response was not JSON:', e);
          throw new Error('Invalid response from server');
        }
        setStudent(data.student || null);
        // Fetch this student's saved grades (supplemental — don't fail the page)
        try {
          const gres = await fetch(`/api/data-points/student/${studentId}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (gres.ok) {
            const gdata = await gres.json();
            setGrades(Array.isArray(gdata) ? gdata : []);
          }
        } catch (_: any) { /* grades are supplemental */ }
      } catch (err: any) {
        setError('Could not load student profile.');
        setStudent(null);
      } finally {
        setLoading(false);
      }
    }
    if (token && studentId) loadStudent();
  }, [studentId, token]);

  if (loading) return <div className="p-8">Loading student profile...</div>;

  return (
    <div className="p-8">
      <PageBackNavigation onBack={handleBack} label={returnLabel} />

      {error && (
        <div style={{ color: '#dc2626', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {student ? (
        <div style={{ maxWidth: 800 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 20 }}>
            {`${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`}
          </h1>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              rowGap: 12,
              columnGap: 20,
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 20,
            }}
          >
            <strong>Student ID</strong>
            <span>{student.student_id}</span>

            <strong>Email</strong>
            <span>{student.email || 'N/A'}</span>

            <strong>Grade Level</strong>
            <span>{student.grade_level ?? 'N/A'}</span>

            {student.external_student_id && (
              <>
                <strong>External ID</strong>
                <span>{student.external_student_id}</span>
              </>
            )}

            {student.enrollment_date && (
              <>
                <strong>Enrollment Date</strong>
                <span>{student.enrollment_date}</span>
              </>
            )}
          </div>

          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Class Enrollments</h2>
            {Array.isArray(student.classes) && student.classes.length > 0 ? (
              <div>
                {student.classes.map((cls) => (
                  <button
                    key={`${student.student_id}-${cls.class_id}`}
                    onClick={() =>
                      navigate(isReadOnlyTeacherDemo(user) ? `/teacher/student-progress/${studentId}/${cls.class_id}` : '/teacher/standards-progression', {
                        state: { prefill: { subject: cls.subject_name || cls.course_title || '' } },
                      })
                    }
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 12,
                      marginBottom: 8,
                      background: '#fafafa',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.borderColor = '#6366f1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  >
                    <div style={{ fontWeight: 600 }}>{cls.class_name || `Class ${cls.class_id}`}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {cls.subject_name || cls.course_title || 'Subject N/A'} • Status:{' '}
                      {cls.status || 'N/A'}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div>No active enrollments found.</div>
            )}
          </div>

          {grades.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Recent Grades</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {grades.map((g) => (
                  <div
                    key={g.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '10px 16px',
                      background: (GRADING_STYLES[pointColor(g)] || GRADING_STYLES.gray).bg,
                      borderColor: '#dce4f2',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 13, flex: 1 }}>
                      {g.standard_code || `Standard ${g.standard_id}`}
                    </span>
                    <span
                      style={{
                        background: 'rgba(255,255,255,0.5)',
                        color: '#12203a',
                        borderRadius: 4,
                        padding: '2px 10px',
                        fontWeight: 700,
                        fontSize: 14,
                        minWidth: 48,
                        textAlign: 'center',
                      }}
                    >
                      {g.score !== null && g.score !== undefined ? g.score : '—'}
                    </span>
                    {g.date && (
                      <span
                        style={{
                          color: '#12203a',
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {g.date}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 32 }}>
            <WorkGallery studentId={studentId} />
          </div>

        </div>
      ) : (
        <div>Student not found.</div>
      )}
      <PageBackNavigation onBack={handleBack} label={returnLabel} position="bottom" />
    </div>
  );
}
